import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const FormBuilderSimple = () => {
    const { pageId, formId } = useParams();
    const { api } = useAuth();
    const [form, setForm] = useState(null);
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedField, setSelectedField] = useState(null);
    const [showFieldConfig, setShowFieldConfig] = useState(false);
    const [fieldConfig, setFieldConfig] = useState({
        label: '',
        placeholder: '',
        help_text: '',
        is_required: false,
        options: []
    });
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareLink, setShareLink] = useState('');

    const fetchFormDetails = async () => {
        try {
            const formRes = await api.get(`/api/pages/${pageId}/forms/${formId}/`);
            setForm(formRes.data);
            const fieldsRes = await api.get(`/api/pages/${pageId}/forms/${formId}/fields/`);
            setFields(fieldsRes.data);
        } catch (error) {
            console.error("Failed to fetch form details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFormDetails();
    }, [pageId, formId, api]);

    const handleAddField = async (fieldType) => {
        // Reset field configuration
        setFieldConfig({
            label: `New ${fieldType}`,
            placeholder: '',
            help_text: '',
            is_required: false,
            options: []
        });
        setSelectedField({ field_type: fieldType });
        setShowFieldConfig(true);
    };

    const handleSaveField = async () => {
        const newField = {
            label: fieldConfig.label,
            field_type: selectedField.field_type,
            order: fields.length,
            is_required: fieldConfig.is_required,
            placeholder: fieldConfig.placeholder,
            help_text: fieldConfig.help_text,
            config: {},
            options: fieldConfig.options
        };

        try {
            const response = await api.post(`/api/pages/${pageId}/forms/${formId}/fields/`, newField);
            setFields([...fields, response.data]);
            setShowFieldConfig(false);
            setSelectedField(null);
            setFieldConfig({
                label: '',
                placeholder: '',
                help_text: '',
                is_required: false,
                options: []
            });
        } catch (error) {
            console.error("Failed to add field:", error);
        }
    };

    const handleUpdateField = async (fieldId, updatedData) => {
        try {
            const response = await api.patch(`/api/pages/${pageId}/forms/${formId}/fields/${fieldId}/`, updatedData);
            setFields(fields.map(field => field.id === fieldId ? response.data : field));
        } catch (error) {
            console.error("Failed to update field:", error);
        }
    };

    const handleDeleteField = async (fieldId) => {
        try {
            await api.delete(`/api/pages/${pageId}/forms/${formId}/fields/${fieldId}/`);
            setFields(fields.filter(field => field.id !== fieldId));
        } catch (error) {
            console.error("Failed to delete field:", error);
        }
    };

    const handleShareForm = async () => {
        try {
            // Generate or get the public link
            const response = await api.post(`/api/pages/${pageId}/forms/${formId}/generate_public_link/`);
            const publicLink = `${window.location.protocol}//${window.location.host}/form/${response.data.public_link_id}`;
            setShareLink(publicLink);
            setShowShareModal(true);
        } catch (error) {
            console.error("Failed to generate share link:", error);
        }
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(shareLink);
            alert('Link copied to clipboard!');
        } catch (error) {
            console.error("Failed to copy link:", error);
        }
    };

    if (loading) return <div className="text-center p-8">Loading form builder...</div>;
    if (!form) return <div className="text-center p-8">Form not found</div>;

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <header className="bg-white shadow">
                <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center">
                        <div>
                            <Link to={`/page/${pageId}`} className="text-sm text-indigo-600 hover:underline mb-2 block">
                                &larr; Back to Page
                            </Link>
                            <h1 className="text-2xl font-bold text-gray-900">{form.title} - Form Builder</h1>
                        </div>
                        <div className="flex space-x-2">
                            <div className="flex items-center mr-4">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    form.is_active 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-red-100 text-red-800'
                                }`}>
                                    {form.is_active ? '🟢 Active' : '🔴 Inactive'}
                                </span>
                            </div>
                            <div className="flex items-center mr-4">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    📊 {form.submissions_count || 0} Submissions
                                </span>
                            </div>
                            <button
                                onClick={handleShareForm}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                                disabled={!form.is_active}
                            >
                                📤 Share Form
                            </button>
                            <button
                                onClick={() => window.open(`/api/pages/${pageId}/forms/${formId}/download_excel/`, '_blank')}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                📥 Download Submissions
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="flex h-screen">
                {/* Field Palette */}
                <div className="w-80 bg-white shadow-lg overflow-y-auto">
                    <div className="p-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Add Fields</h2>
                    </div>
                    <div className="p-4">
                        <div className="space-y-2">
                            <button 
                                onClick={() => handleAddField('text')}
                                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50"
                            >
                                <div className="font-medium">📄 Text Field</div>
                                <div className="text-sm text-gray-500">Single line text input</div>
                            </button>
                            
                            <button 
                                onClick={() => handleAddField('textarea')}
                                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50"
                            >
                                <div className="font-medium">📝 Textarea</div>
                                <div className="text-sm text-gray-500">Multi-line text input</div>
                            </button>
                            
                            <button 
                                onClick={() => handleAddField('email')}
                                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50"
                            >
                                <div className="font-medium">📧 Email</div>
                                <div className="text-sm text-gray-500">Email address input</div>
                            </button>
                            
                            <button 
                                onClick={() => handleAddField('select')}
                                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50"
                            >
                                <div className="font-medium">📋 Dropdown</div>
                                <div className="text-sm text-gray-500">Select from options</div>
                            </button>
                            
                            <button 
                                onClick={() => handleAddField('checkbox')}
                                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50"
                            >
                                <div className="font-medium">☑️ Checkbox</div>
                                <div className="text-sm text-gray-500">Yes/No option</div>
                            </button>
                            
                            <button 
                                onClick={() => handleAddField('date')}
                                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50"
                            >
                                <div className="font-medium">📅 Date Picker</div>
                                <div className="text-sm text-gray-500">Select a date</div>
                            </button>

                            <button 
                                onClick={() => handleAddField('number')}
                                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50"
                            >
                                <div className="font-medium">🔢 Number</div>
                                <div className="text-sm text-gray-500">Numeric input</div>
                            </button>

                            <button 
                                onClick={() => handleAddField('phone')}
                                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50"
                            >
                                <div className="font-medium">📞 Phone</div>
                                <div className="text-sm text-gray-500">Phone number input</div>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Form Canvas */}
                <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-[600px]">
                            {/* Form Header */}
                            <div className="p-6 border-b border-gray-200">
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">{form.title}</h1>
                                {form.description && (
                                    <p className="text-gray-600">{form.description}</p>
                                )}
                            </div>

                            {/* Form Fields */}
                            <div className="p-6">
                                {fields.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="text-gray-400 text-6xl mb-4">📝</div>
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                                            Start building your form
                                        </h3>
                                        <p className="text-gray-500">
                                            Click on the fields in the left panel to add them to your form
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {fields.map((field, index) => (
                                            <div key={field.id} className="border border-gray-200 rounded-lg p-4 relative group">
                                                {/* Field Actions */}
                                                <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={() => {
                                                            setFieldConfig({
                                                                label: field.label,
                                                                placeholder: field.placeholder || '',
                                                                help_text: field.help_text || '',
                                                                is_required: field.is_required,
                                                                options: field.options || []
                                                            });
                                                            setSelectedField(field);
                                                            setShowFieldConfig(true);
                                                        }}
                                                        className="p-1 text-gray-400 hover:text-indigo-600 bg-white rounded shadow-sm"
                                                        title="Edit field"
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteField(field.id)}
                                                        className="p-1 text-gray-400 hover:text-red-600 bg-white rounded shadow-sm"
                                                        title="Delete field"
                                                    >
                                                        🗑️
                                                    </button>
                                                </div>

                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    {field.label}
                                                    {field.is_required && <span className="text-red-500 ml-1">*</span>}
                                                </label>
                                                
                                                {field.field_type === 'text' && (
                                                    <input 
                                                        type="text" 
                                                        placeholder={field.placeholder || ''}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                        disabled
                                                    />
                                                )}
                                                
                                                {field.field_type === 'textarea' && (
                                                    <textarea 
                                                        placeholder={field.placeholder || ''}
                                                        rows={3}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                        disabled
                                                    />
                                                )}
                                                
                                                {field.field_type === 'email' && (
                                                    <input 
                                                        type="email" 
                                                        placeholder={field.placeholder || ''}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                        disabled
                                                    />
                                                )}
                                                
                                                {field.field_type === 'select' && (
                                                    <select className="w-full px-3 py-2 border border-gray-300 rounded-md" disabled>
                                                        <option>{field.placeholder || 'Select an option'}</option>
                                                        {field.options?.map((option, idx) => (
                                                            <option key={idx} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                                
                                                {field.field_type === 'checkbox' && (
                                                    <label className="flex items-center">
                                                        <input type="checkbox" className="mr-2" disabled />
                                                        {field.label}
                                                    </label>
                                                )}
                                                
                                                {field.field_type === 'date' && (
                                                    <input 
                                                        type="date" 
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                        disabled
                                                    />
                                                )}

                                                {field.field_type === 'number' && (
                                                    <input 
                                                        type="number" 
                                                        placeholder={field.placeholder || ''}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                        disabled
                                                    />
                                                )}

                                                {field.field_type === 'phone' && (
                                                    <input 
                                                        type="tel" 
                                                        placeholder={field.placeholder || ''}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                                        disabled
                                                    />
                                                )}
                                                
                                                {field.help_text && (
                                                    <p className="mt-1 text-sm text-gray-500">{field.help_text}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Field Configuration Modal */}
            {showFieldConfig && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                            {selectedField.id ? 'Edit Field' : 'Configure New Field'}
                        </h3>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Field Label *
                                </label>
                                <input
                                    type="text"
                                    value={fieldConfig.label}
                                    onChange={(e) => setFieldConfig({...fieldConfig, label: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Enter field label"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Placeholder Text
                                </label>
                                <input
                                    type="text"
                                    value={fieldConfig.placeholder}
                                    onChange={(e) => setFieldConfig({...fieldConfig, placeholder: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Enter placeholder text"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Help Text
                                </label>
                                <textarea
                                    value={fieldConfig.help_text}
                                    onChange={(e) => setFieldConfig({...fieldConfig, help_text: e.target.value})}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Enter help text"
                                />
                            </div>

                            <div>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={fieldConfig.is_required}
                                        onChange={(e) => setFieldConfig({...fieldConfig, is_required: e.target.checked})}
                                        className="mr-2"
                                    />
                                    <span className="text-sm font-medium text-gray-700">
                                        Required field
                                    </span>
                                </label>
                            </div>

                            {/* Options for select fields */}
                            {(selectedField.field_type === 'select' || selectedField.field_type === 'checkbox') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Options
                                    </label>
                                    <div className="space-y-2">
                                        {fieldConfig.options.map((option, index) => (
                                            <div key={index} className="flex space-x-2">
                                                <input
                                                    type="text"
                                                    placeholder="Option label"
                                                    value={option.label}
                                                    onChange={(e) => {
                                                        const newOptions = [...fieldConfig.options];
                                                        newOptions[index].label = e.target.value;
                                                        setFieldConfig({...fieldConfig, options: newOptions});
                                                    }}
                                                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Value"
                                                    value={option.value}
                                                    onChange={(e) => {
                                                        const newOptions = [...fieldConfig.options];
                                                        newOptions[index].value = e.target.value;
                                                        setFieldConfig({...fieldConfig, options: newOptions});
                                                    }}
                                                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                                />
                                                <button
                                                    onClick={() => {
                                                        const newOptions = fieldConfig.options.filter((_, i) => i !== index);
                                                        setFieldConfig({...fieldConfig, options: newOptions});
                                                    }}
                                                    className="px-2 py-1 text-red-600 hover:text-red-800"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            onClick={() => setFieldConfig({
                                                ...fieldConfig, 
                                                options: [...fieldConfig.options, { label: '', value: '' }]
                                            })}
                                            className="w-full px-3 py-2 border border-dashed border-gray-300 rounded-md text-gray-500 hover:text-gray-700 hover:border-gray-400"
                                        >
                                            + Add Option
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowFieldConfig(false);
                                    setSelectedField(null);
                                    setFieldConfig({
                                        label: '',
                                        placeholder: '',
                                        help_text: '',
                                        is_required: false,
                                        options: []
                                    });
                                }}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={selectedField.id ? 
                                    () => {
                                        handleUpdateField(selectedField.id, fieldConfig);
                                        setShowFieldConfig(false);
                                        setSelectedField(null);
                                    } : 
                                    handleSaveField
                                }
                                disabled={!fieldConfig.label.trim()}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {selectedField.id ? 'Update Field' : 'Save Field'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Share Form Modal */}
            {showShareModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                            📤 Share Form with Society Members
                        </h3>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Form Link
                                </label>
                                <div className="flex">
                                    <input
                                        type="text"
                                        value={shareLink}
                                        readOnly
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50 text-sm"
                                    />
                                    <button
                                        onClick={copyToClipboard}
                                        className="px-4 py-2 bg-indigo-600 text-white rounded-r-md hover:bg-indigo-700"
                                    >
                                        Copy
                                    </button>
                                </div>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                                <h4 className="font-medium text-blue-900 mb-2">📋 How to Share:</h4>
                                <ul className="text-sm text-blue-800 space-y-1">
                                    <li>• Share this link with society members via WhatsApp, email, or SMS</li>
                                    <li>• Members can fill and submit the form without logging in</li>
                                    <li>• You'll receive all submissions in your dashboard</li>
                                    <li>• The link will work until you disable the form</li>
                                </ul>
                            </div>

                            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                                <h4 className="font-medium text-yellow-900 mb-2">⚠️ Important Notes:</h4>
                                <ul className="text-sm text-yellow-800 space-y-1">
                                    <li>• Anyone with this link can submit the form</li>
                                    <li>• Keep the link private if the form contains sensitive information</li>
                                    <li>• You can disable the form anytime from your dashboard</li>
                                </ul>
                            </div>

                            <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                                <h4 className="font-medium text-gray-900 mb-2">⚙️ Form Settings:</h4>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={form.is_active}
                                            onChange={async (e) => {
                                                try {
                                                    await api.patch(`/api/pages/${pageId}/forms/${formId}/`, {
                                                        is_active: e.target.checked
                                                    });
                                                    // Refresh form data
                                                    fetchFormDetails();
                                                } catch (error) {
                                                    console.error("Failed to update form status:", error);
                                                }
                                            }}
                                            className="mr-2"
                                        />
                                        <span className="text-sm text-gray-700">
                                            Form is active (accepting submissions)
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                onClick={() => setShowShareModal(false)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    copyToClipboard();
                                    setShowShareModal(false);
                                }}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                            >
                                Copy & Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FormBuilderSimple; 